//frontend/src/components/BranstonConverter.js
import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  IconButton,
  Alert,
  Fade,
  Stack
} from '@mui/material';
import { UploadFile as UploadFileIcon, RestartAlt, CheckCircle, ErrorOutlineOutlined as ErrorOutline } from '@mui/icons-material';
import Layout from './Layout';
import { convertBranstonCsv } from '../services/api';

export default function BranstonConverter() {
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const fileInputRef = useRef();
  const dragDepthRef = useRef(0);

  // Handle file from input or drop
  const handleFile = f => {
    if (!f) return;
    setFile(f);
    setStatus(null);
    setErrorMsg('');
  };

  // Native input change
  const handleInputChange = e => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const isFileDrag = e => Array.from(e.dataTransfer?.types || []).includes('Files');

  const handleDragEnter = e => {
    e.preventDefault();
    e.stopPropagation();
    if (!isFileDrag(e) || loading) return;
    dragDepthRef.current += 1;
    setDragActive(true);
  };

  const handleDragOver = e => {
    e.preventDefault();
    e.stopPropagation();
    if (!isFileDrag(e) || loading) return;
    e.dataTransfer.dropEffect = 'copy';
    if (!dragActive) setDragActive(true);
  };

  const handleDragLeave = e => {
    e.preventDefault();
    e.stopPropagation();
    if (!isFileDrag(e)) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDragActive(false);
  };

  // Drop event
  const handleDrop = e => {
    e.preventDefault();
    e.stopPropagation();
    if (loading) return;
    dragDepthRef.current = 0;
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleReset = () => {
    setFile(null);
    setStatus(null);
    setErrorMsg('');
    if (fileInputRef.current) fileInputRef.current.value = null;
  };

  const handleSubmit = async () => {
    if (!file) return;
    setLoading(true);
    setStatus(null);
    setErrorMsg('');
    try {
      const blob = await convertBranstonCsv(file);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'branston.xml';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setStatus('success');
    } catch (err) {
      setErrorMsg('Conversion failed. Please check the CSV format or try again.');
      setStatus('error');
    } finally {
      setLoading(false);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = null;
    }
  };

  return (
    <Layout>
      <Box sx={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'background.default'
      }}>
        <Paper elevation={3}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            minWidth: { xs: 320, sm: 450 },
            width: '100%',
            maxWidth: 450,
            mx: 'auto',
            boxShadow: '0 4px 24px 0 #1540630e',
            textAlign: 'center',
            transition: 'box-shadow .3s',
            position: "relative",
            overflow: "visible"
          }}
        >
          <form
            onSubmit={e => e.preventDefault()}
            style={{ position: "relative" }}
            autoComplete="off"
          >
            <Box sx={{ mb: 2 }}>
              <UploadFileIcon
                sx={{
                  fontSize: 56,
                  color: theme => theme.palette.primary.main,
                  mb: 1,
                }}
              />
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Branston CSV → XML Converter
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Drag and drop your Branston <b>CSV</b> file here,<br />
                or <span style={{ color: "#2294f3", fontWeight: 500 }}>click to select</span> from your device.
              </Typography>
            </Box>
            <Box
              onClick={() => {
                if (loading) return;
                if (fileInputRef.current) fileInputRef.current.click();
              }}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                cursor: 'pointer',
                p: 2.5,
                mb: 2,
                border: '2px dashed',
                borderColor: dragActive
                  ? theme => theme.palette.primary.main
                  : theme => theme.palette.divider,
                borderRadius: 3,
                bgcolor: dragActive
                  ? theme => theme.palette.action.hover
                  : theme => theme.palette.background.paper,
                transition: "border-color .2s, background .2s",
                minHeight: 96,
                outline: "none",
                position: "relative",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                "&:hover": {
                  borderColor: theme => theme.palette.primary.main,
                  background: theme => theme.palette.action.hover,
                }
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                style={{ display: "none" }}
                onChange={handleInputChange}
                disabled={loading}
              />
              {file ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="body2"
                    color="primary"
                    fontWeight={500}
                    sx={{
                      maxWidth: 200,
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                    title={file.name}
                  >
                    {file.name}
                  </Typography>
                  <IconButton size="small" color="secondary" onClick={e => { e.stopPropagation(); handleReset(); }}>
                    <RestartAlt fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  color={dragActive ? "primary" : "text.secondary"}
                  sx={{ fontWeight: 500 }}
                >
                  {dragActive
                    ? "Release to upload CSV file"
                    : "Drop or click to select your CSV file"}
                </Typography>
              )}
            </Box>

            <Stack spacing={2}>
              <Button
                variant="contained"
                color="primary"
                disabled={!file || loading}
                onClick={handleSubmit}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.4,
                  fontWeight: 600,
                  fontSize: 16,
                  mt: 0.5
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Convert & Download"
                )}
              </Button>
              <Fade in={!!status}>
                <Box>
                  {status === 'success' && (
                    <Alert
                      icon={<CheckCircle fontSize="inherit" />}
                      severity="success"
                      sx={{
                        mt: 2, borderRadius: 2, fontWeight: 500,
                        justifyContent: "center",
                        alignItems: "center"
                      }}
                    >
                      Success! Your XML has downloaded.
                    </Alert>
                  )}
                  {status === 'error' && (
                    <Alert
                      icon={<ErrorOutline fontSize="inherit" />}
                      severity="error"
                      sx={{
                        mt: 2, borderRadius: 2, fontWeight: 500,
                        justifyContent: "center",
                        alignItems: "center"
                      }}
                    >
                      {errorMsg}
                    </Alert>
                  )}
                </Box>
              </Fade>
            </Stack>
          </form>
        </Paper>
      </Box>
    </Layout>
  );
}

